import { PropsWithChildren } from "react";

import { Typography, TypographyProps } from "@aircall/tractor";
import { useTranslation } from "react-i18next";

import { EvaluationQuestionSummaryRatingProps, StyledFlex } from ".";

type Colors = TypographyProps["color"];

function getColorFromValue(value: string): Colors {
  switch (value) {
    case "YES":
      return "text-success";
    case "NO":
      return "text-critical";
    case "EXCELLENT":
      return "text-success";
    case "GOOD":
      return "text-success";
    case "FAIR":
      return "text-warning";
    case "POOR":
      return "text-critical";
    case "VERY_POOR":
      return "text-critical";
    default:
      return "text-secondary";
  }
}

function getLabelFromValue(value: string): string {
  switch (value) {
    case "YES":
      return "yes";
    case "NO":
      return "no";
    case "EXCELLENT":
      return "excellent";
    case "GOOD":
      return "good";
    case "FAIR":
      return "fair";
    case "POOR":
      return "poor";
    case "VERY_POOR":
      return "very_poor";
    default:
      return "";
  }
}

function Container<Value extends string>({
  children,
  disabled,
}: PropsWithChildren<
  Pick<EvaluationQuestionSummaryRatingProps<Value>, "disabled">
>) {
  return (
    <StyledFlex
      disabled={disabled}
      flexDirection="column"
      alignItems="center"
      gap="xxxs"
      p="s"
      minWidth="120px"
      flexShrink={0}
    >
      {children}
    </StyledFlex>
  );
}

export function EvaluationQuestionSummaryRating<Value extends string>({
  value,
  maximumPoints,
  points,
  disabled,
}: EvaluationQuestionSummaryRatingProps<Value>) {
  const { t } = useTranslation();

  if (disabled) {
    return (
      <Container disabled={disabled}>
        <Typography variant="headingBoldS" color="text-disabled">
          {t("not_applicable")}
        </Typography>
        <Typography variant="supportingRegularS" color="text-disabled">
          {t("evaluation.question_summary_points", {
            text: "--",
          })}
        </Typography>
      </Container>
    );
  }

  if (!value) {
    return null;
  }

  const color = getColorFromValue(value);
  const label = getLabelFromValue(value);

  return (
    <Container>
      <Typography variant="headingBoldS" color={color}>
        {t(label)}
      </Typography>
      <Typography variant="supportingRegularS" color="text-secondary">
        {t("evaluation.question_summary_points", {
          text: `${points ?? "--"} / ${maximumPoints}`,
        })}
      </Typography>
    </Container>
  );
}
